import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isNil from 'utils/isNil';
import WebsiteImage from 'components/WebsiteImage';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import Slider from 'react-slick';
import Text from 'components/Core/Text';
import WistiaVideo from 'components/Core/WistiaVideo';

const InPageFeaturesV1 = ({
  title,
  description,
  inPageNavigationItems,
  backgroundColor,
  bg,
  ...otherProps
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  let slider;
  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    draggable: false,
    touchMove: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    accessibility: false,
    cssEase: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
  };

  const onNavItemClick = (index) => {
    if (index >= 0) {
      setSelectedIndex(index);

      slider.slickGoTo(index, true);
    }
  };

  return (
    <SectionSoftware backgroundColor={backgroundColor} id="in-page-features-v1" {...otherProps}>
      <div className="section-software--container">
        <div className="section-software--header-container">
          <Text type="p" textSize="h1" className="section-software--title">
            {title}
          </Text>
          <p className="section-software--description">{description}</p>
        </div>

        <div className="section-software--content-container">
          <div className="section-software--navigation-content">
            {inPageNavigationItems?.map((item, index) => {
              return (
                <div
                  className={`section-software--navigation-item item-${index} ${
                    selectedIndex === index ? 'active' : 'inactive'
                  }`}
                  onClick={() => onNavItemClick(index)}
                >
                  <>
                    <div className="section-software--item-header">
                      <div className="section-software--item-header-content">
                        {item.icon && (
                          <WebsiteImage
                            className="section-software--navigation-item-icon"
                            {...item.icon}
                          />
                        )}
                        <h2 className="section-software--navigation-item-title in-page-features-link">
                          {item.title}
                        </h2>
                      </div>
                      <div className="section-software--item-header-content-additional" />
                    </div>
                  </>
                  {!isNil(item.description) ? (
                    <p className="section-software--navigation-item-description">
                      {renderRichText(item.description)}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="section-software--main-content">
            <Slider ref={(c) => (slider = c)} {...sliderSettings}>
              {inPageNavigationItems?.map((item, index) => {
                return item.featuredImage ? (
                  <div className="section-software--image-content" key={`image-content-${index}`}>
                    <WebsiteImage {...item.featuredImage} />
                  </div>
                ) : item.featuredVideo ? (
                  <div className="section-software--video-content" key={`video-content-${index}`}>
                    <WistiaVideo
                      dataPosition="In-Page Features"
                      {...item.featuredVideo}
                      wrapperStyles={{ width: '100%', height: '450px' }}
                    />
                  </div>
                ) : (
                  <></>
                );
              })}
            </Slider>
          </div>

          <div style={{ display: 'none' }}>
            {inPageNavigationItems?.map((item, index) => {
              return !isNil(item.description) ? (
                <div key={`title-description-${index}`}>
                  <h2>{item.title}</h2>
                  <p>{renderRichText(item.description)}</p>
                </div>
              ) : null;
            })}
          </div>
        </div>
      </div>
    </SectionSoftware>
  );
};

InPageFeaturesV1.propTypes = {
  backgroundColor: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  inPageNavigationItems: PropTypes.array,
};

const SectionSoftware = styled.div`
  background: ${(props) => 'var(' + props.backgroundColor + ')'};

  .section-software--item-header-content {
    &:hover {
      cursor: pointer;
    }
  }

  .inactive {
    .section-software--navigation-item-description {
      display: none;
    }
    .section-software--item-header
      .section-software--item-header-content
      .section-software--navigation-item-title {
      color: var(--darkest) !important;
    }
  }

  .active {
    .section-software--navigation-item-description {
      display: block;
    }
    .section-software--item-header-content {
      box-shadow: 0px 3px 10px rgba(11, 26, 33, 0.08);
    }

    .section-software--item-header-content-additional {
      border-left: 30px solid rgb(255, 255, 255);
      border-top: 41px solid transparent;
      border-bottom: 41px solid transparent;
      content: '';
      height: 0;
      top: 0;
      right: -41px;
      width: 0;
      filter: drop-shadow(rgba(11, 26, 33, 0.03) 6px 4px 3px);
    }
  }

  .section-software--container {
    max-width: 1270px;
    padding: 98px 20px 107px;
    margin: 0 auto;

    .section-software--header-container {
      max-width: 784px;
      text-align: center;
      margin: 0 auto;

      .section-software--title {
        font-size: var(--font-size-44);
        line-height: var(--line-height-110);
        text-align: center;
        letter-spacing: var(--letter-spacing--0-04);
        font-weight: var(--font-weight-900);
        color: var(--darkest);
      }

      .section-software--description {
        font-size: var(--font-size-20);
        line-height: var(--line-height-150);
        text-align: center;
        color: var(--dark);
      }
    }

    .section-software--content-container {
      display: flex;
      align-items: center;
      margin-top: 30px;

      .section-software--navigation-content {
        width: 100%;
        flex: 5;

        .section-software--navigation-item {
          .section-software--item-header {
            display: flex;
            width: max-content;

            .section-software--item-header-content {
              display: flex;
              height: 82px;
              position: relative;
              width: max-content;
              align-items: center;
              padding-left: 40px;

              .section-software--navigation-item-icon {
                width: 40px;
              }

              .section-software--navigation-item-title {
                margin-left: 17px;
                margin-bottom: -3px;
                color: var(--darkest);
              }

              &:hover {
                .section-software--navigation-item-icon {
                  opacity: 0.8;
                }
                .section-software--navigation-item-title {
                  opacity: 0.8;
                }
              }
            }
          }

          .section-software--navigation-item-description {
            font-size: var(--font-size-16);
            line-height: var(--line-height-150);
            padding: 31px 35px 31px 40px;
            margin: 0;
            color: var(--darkest);
          }
        }
      }

      .section-software--main-content {
        flex: 7;
        padding-left: 50px;
        overflow: hidden;

        .section-software--image-content,
        .section-software--video-content {
          width: max-content;
          overflow: hidden;

          img {
            max-width: 100%;
            max-height: 600px;
            margin: auto;
          }
        }
      }
    }
  }

  @media (max-width: 991px) {
    .section-software--container {
      padding: 65px 20px;
      max-width: 580px;

      .section-software--header-container {
        .section-software--title {
          font-size: var(--font-size-35);
          line-height: 44px;
        }

        .section-software--description {
          font-size: var(--font-size-18);
          line-height: 28px;
          margin-top: 22px;
          padding: 0 10px;
        }
      }

      .section-software--content-container {
        margin-top: 42px;

        .section-software--navigation-content {
          .section-software--navigation-item {
            .section-software--item-header {
              .section-software--item-header-content {
                height: 82px;
                padding-left: 20px;

                .section-software--navigation-item-title {
                  font-size: var(--font-size-20);
                  line-height: 26px;
                  margin-left: 19px;
                }
              }
            }

            .section-software--navigation-item-description {
              font-size: var(--font-size-18);
              line-height: 28px;
              padding: 29px 20px;
            }
          }
        }

        .section-software--main-content {
          display: none;
        }
      }
    }
  }

  @media (max-width: 575px) {
    .section-software--container {
      padding: 67px 20px 75px;

      .section-software--header-container {
        .section-software--title {
          font-size: var(--font-size-28);
          line-height: 36px;
        }

        .section-software--description {
          font-size: var(--font-size-28);
          line-height: 28px;
          padding: 0;
        }
      }

      .section-software--content-container {
        .section-software--navigation-content {
          .section-software--navigation-item {
            .section-software--item-header {
              .section-software--item-header-content {
                height: 82px;
                padding-left: 22px;

                .section-software--navigation-item-title {
                  font-size: var(--font-size-18);
                  line-height: 16px;
                  margin-left: 13px;
                }
              }
            }

            .section-software--navigation-item-description {
              font-size: var(--font-size-16);
              line-height: 28px;
              padding: 32px 22px;
            }
          }
        }
      }
    }
  }

  .section-software--item-header {
    .section-software--item-header-content {
      background: var(--white);

      .section-software--navigation-item-title {
        color: var(--titan-blue-3) !important;
      }

      svg > path {
        fill: var(--titan-blue-3) !important;
      }
    }
  }
`;

export default InPageFeaturesV1;
